import { Divider } from '@vrplatform/components';
import {
  useBilling,
  useDashboard,
  useMe,
  useOwnerPortalContact,
  useQuery,
  useTeam,
  useTeamRole,
} from '@vrplatform/data';
import {
  ApiTokensIcon,
  AutomationsIcon,
  BillingIcon,
  BracketsIcon,
  DashboardIcon,
  FileTextIcon,
  FingerprintIcon,
  Icon,
  MembersIcon,
  NotificationIcon,
  OwnerPortalIcon,
  OwnerStatementsIcon,
  PercentageCircleIcon,
  UserIcon,
} from '@vrplatform/icons';
import { Box, Center, Skeleton, Stack } from '@mantine/core';
import { HiddenFeatureIndicator } from '../components';
import { NavbarRoute } from '../layout/navbar';
import { getOwnerPortalTaxStatementSetting } from '@vrplatform/data';
import { NavigationSection } from './NavigationSection';

export const SettingsNavigation = () => {
  const [dashboard] = useDashboard();
  const { isTeamAdmin } = useTeamRole();
  const [{ membershipId, isOnboarding, isGL: GL }] = useTeam();
  const { showBilling, isXimplifiCustomer } = useBilling();

  const isPmDashboard = dashboard === 'propertyManager';
  const isOwnerPortal = dashboard === 'owner';

  if (isOwnerPortal) {
    return (
      <Stack gap="lg">
        <OwnerSettingsNavigation />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <NavigationSection title={isOwnerPortal ? '' : 'User Account'}>
        <NavbarRoute
          title="Profile & Security"
          link="/settings/profile"
          icon={<UserIcon size={20} />}
        />
        <HiddenFeatureIndicator permission="vrp-admin">
          <NavbarRoute
            title="Debugging"
            link="/settings/developer"
            icon={<Icon icon="GearIcon" size={18} />}
          />
        </HiddenFeatureIndicator>

        {isOwnerPortal && <OwnerSettingsNavigation />}
      </NavigationSection>

      {!isOwnerPortal && dashboard !== 'overview' && (
        <>
          <Divider my={0} opacity={0.1} />

          <NavigationSection title="Team">
            <NavbarRoute
              title="General"
              link="/settings/team/general"
              icon={<DashboardIcon size={20} />}
            />

            {isTeamAdmin && (
              <NavbarRoute
                title="Security"
                link="/settings/team/security"
                icon={<FingerprintIcon size={20} />}
              />
            )}

            {isPmDashboard && (
              <NavbarRoute
                title="Members"
                link="/settings/team/members"
                icon={<MembersIcon />}
              />
            )}
            {showBilling && isTeamAdmin && !isXimplifiCustomer && (
              <NavbarRoute
                title="Billing"
                link={'/settings/team/billing'}
                icon={<BillingIcon />}
              />
            )}
            {isPmDashboard && !isOnboarding && (
              <HiddenFeatureIndicator permission="super-admin">
                <NavbarRoute
                  title="Automations"
                  link="/settings/team/automations"
                  icon={<AutomationsIcon size={20} />}
                />
              </HiddenFeatureIndicator>
            )}
            {GL && membershipId && !isOnboarding && (
              <NavbarRoute
                title="Notifications"
                link="/settings/team/notifications"
                icon={<Icon icon="NotificationIcon" size={18} />}
              />
            )}
          </NavigationSection>

          {isPmDashboard && !isOnboarding && GL && (
            <NavigationSection title="Accounting">
              <NavbarRoute
                title="Books Closed Date"
                link="/settings/accounting/books-closed"
                icon={<Icon icon="Lock2Icon" size={18} />}
              />
              <NavbarRoute
                title="Accounts"
                link="/settings/accounting/accounts"
                icon={<Icon icon="ListUnorderedIcon" size={18} />}
              />
              <NavbarRoute
                title="Reservations"
                link="/settings/accounting/reservations"
                icon={<Icon icon="CalendarEventIcon" size={18} />}
              />
              <NavbarRoute
                title="Tax Rates"
                link="/settings/accounting/tax-rates"
                icon={<PercentageCircleIcon size={18} />}
              />
              <NavbarRoute
                title="Opening Balances"
                link="/settings/accounting/opening-balances"
                icon={<Icon icon="CircleDollarIcon" size={18} />}
              />
            </NavigationSection>
          )}

          {isPmDashboard && !isOnboarding && (
            <NavigationSection title="Features">
              {GL && (
                <HiddenFeatureIndicator permission="super-admin">
                  <NavbarRoute
                    title="PM Statements"
                    link="/settings/features/pm-statements"
                    icon={<Icon icon="FileCheckIcon" size={18} />}
                  />
                </HiddenFeatureIndicator>
              )}

              <NavbarRoute
                title="Owner Portal"
                link="/settings/features/owner-portal"
                icon={<OwnerPortalIcon />}
              />

              <NavbarRoute
                title="Emails"
                link="/settings/features/emails"
                icon={<Icon icon="EmailIcon" size={18} />}
              />

              <HiddenFeatureIndicator permission="super-admin">
                <NavbarRoute
                  title="Owner Statements"
                  link="/settings/features/owner-statements"
                  icon={<OwnerStatementsIcon />}
                />
              </HiddenFeatureIndicator>
            </NavigationSection>
          )}

          {!isOnboarding && (
            <NavigationSection title="Integrations">
              <NavbarRoute
                title="Api tokens"
                link="/settings/integrations/api-tokens"
                icon={<ApiTokensIcon />}
              />
              {isPmDashboard && (
                <NavbarRoute
                  title="Webhooks"
                  link="/settings/integrations/webhooks"
                  icon={<BracketsIcon size={18} />}
                />
              )}
            </NavigationSection>
          )}
        </>
      )}
    </Stack>
  );
};

const OwnerSettingsNavigation = () => {
  const { id: meId } = useMe();
  const [{ isGL: GL, id: teamId, billingAddress: teamBillingAddress }] =
    useTeam();

  const { contactId } = useOwnerPortalContact();

  const { data, isInitialLoading } = useQuery(
    (q, args) => {
      const hasOwnerAccess = !!q
        .ownerUserAccesses({
          where: {
            userId: { _eq: args.meId },
            role: { _eq: 'admin' },
            _or: [
              {
                ownerId: { _eq: args.contactId },
              },
              {
                contactId: { _eq: args.contactId },
              },
            ],
          },
          limit: 1,
        })
        .map((x) => x.id).length;

      const showTaxStatements = getOwnerPortalTaxStatementSetting(q, args);

      return {
        hasOwnerAccess,
        showTaxStatements,
      };
    },
    {
      keepPreviousData: true,
      variables: {
        meId,
        teamId,
        contactId,
        teamBillingAddressCountryCode: teamBillingAddress?.countryCode,
      },
    }
  );

  return (
    <>
      {!GL && (
        <>
          <NavbarRoute
            title="Profile & Security"
            link="/settings/profile"
            icon={<UserIcon size={20} />}
          />
          <NavbarRoute
            title="Notifications"
            link="/settings/notifications"
            icon={
              <Center w={20}>
                <NotificationIcon size={16} />
              </Center>
            }
          />
        </>
      )}

      {!data || isInitialLoading ? (
        <Box px="xs" mt="xs">
          <Skeleton
            height={20}
            animate
            sx={{
              '&::before': {
                backgroundColor: 'unset',
              },
            }}
          />
        </Box>
      ) : (
        <NavigationSection>
          {data?.hasOwnerAccess && (
            <NavbarRoute
              title={GL ? 'General' : 'Statement Details'}
              link="/settings/details"
              icon={
                GL ? (
                  <UserIcon size={20} />
                ) : (
                  <FileTextIcon size={20} strokeWidth={1.2} />
                )
              }
            />
          )}

          {GL && (
            <NavbarRoute
              title="Security"
              link="/settings/profile#/security"
              icon={<Icon icon="ApiTokensIcon" size={20} />}
            />
          )}
        </NavigationSection>
      )}
    </>
  );
};
